import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppointment } from "../useAppointment";
import AccessDeniedError from "../../../components/ErrorComponents/AccessDeniedError";
import styles from "./AppointmentTab.module.css";
import AppointmentDatePicker from "../DatePicker";
import { Button as AntDButton, Collapse, Input, Radio, Space } from "antd";
import { useTranslation } from "react-i18next";
import { ReactComponent as FilterIcon } from "../../../../assets/svg/filter.svg";
import { ReactComponent as CancelIcon } from "../../../../assets/svg/cancel.svg";
import { ReactComponent as SearchIcon } from "../../../../assets/svg/search.svg";
import Loading from "../../../components/Loading/Loading";
import FilterDrawer from "../../../components/FilterDrawer/FilterDrawer";
import { ReactComponent as BottomArrowIcon } from "../../../../assets/svg/bottom-arrow.svg";
import { useSearchParams } from "react-router-dom";
import _ from "lodash";
import dayjs from "dayjs";
import AppointmentCard from "../../../components/Appointment/AppointmentCard";
import SortModal from "../../../components/Appointment/SortModal";

const sort_items = {
  sort_by_expired: "expired",
};

export const sort_list = Object.entries(sort_items).map((item) => {
  return {
    label: item[1],
    value: item[0],
  };
});

export default function AppointmentTab() {
  const [selectedCategory, setSelectedCategory] = useState({
    id: null,
    name: null,
  });
  const defaultDate = useMemo(
    () => [
      new Date().toISOString(),
      new Date(Date.now() + 1000 * 60 * 60 * 24).toISOString(),
    ],
    []
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("q") || "";
  const start = searchParams.get("start") || defaultDate[0];
  const end = searchParams.get("end") || defaultDate[1];
  const sort = searchParams.get("sort") || undefined;
  const [value, setValue] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [appointmentList, setAppointmentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [error, setError] = useState(false);
  const { appointments, categories } = useAppointment();
  const { t } = useTranslation();
  const [categoryList, setCategoryList] = useState([]);

  useEffect(() => {
    categories(setCategoryList, setLoading, setError);
  }, []);

  useEffect(() => {
    appointments(
      setAppointmentList,
      selectedCategory.id,
      setAppointmentsLoading,
      dayjs(start).format("YYYY-MM-DD"),
      dayjs(end).format("YYYY-MM-DD"),
      search,
      sort,
      setError
    );
  }, [JSON.stringify(selectedCategory), start, end, search, sort]);

  const clearFilter = useCallback(() => {
    setSelectedCategory({
      id: null,
      name: null,
    });
    setValue(null);
    setFiltersOpen(false);
  }, []);

  const submitFilter = useCallback(() => {
    const targetCategory = categoryList.find((cat) => cat.id === value);
    if (targetCategory) {
      setSelectedCategory(targetCategory);
    }
    setFiltersOpen(false);
  }, [value, JSON.stringify(categoryList)]);

  const closeDrawer = useCallback(() => {
    setFiltersOpen(false);
  }, []);

  const debouncedSearchFn = useCallback(
    _.debounce((value) => {
      setSearchParams((prevState) => {
        prevState.set("q", value);
        return prevState;
      });
    }, 500),
    []
  );

  const items = [
    {
      styles: {
        header: {
          fontWeight: "700",
        },
      },
      key: "category",
      label: t("common.category"),
      children: (
        <Radio.Group
          onChange={(e) => setValue(e.target.value)}
          value={value}
          style={{
            width: "100%",
          }}
        >
          <Space
            direction="vertical"
            style={{
              width: "100%",
              gap: "0",
            }}
          >
            {categoryList?.map((product) => (
              <Radio
                value={product.id}
                className={styles.radioLabel}
                style={{
                  display: "flex",
                  flexDirection: "row-reverse",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                {product.name}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      ),
    },
  ];

  if (error) {
    return <AccessDeniedError />;
  }

  return (
    <>
      <div className={styles.filterHeader}>
        <Input
          defaultValue={search}
          onChange={(e) => {
            debouncedSearchFn(e.target.value);
          }}
          variant="borderless"
          className={styles.searchInput}
          prefix={<SearchIcon className={styles.searchIcon} />}
          placeholder={t("common.search")}
        />
        <SortModal
          onSubmit={(value) => {
            setSearchParams((prev) => {
              prev.set("sort", value);
              return prev;
            });
          }}
          value={sort || undefined}
          shouldClear={!sort}
        />
        <AntDButton
          className={styles.filterButton}
          type="text"
          onClick={() => {
            setFiltersOpen(true);
          }}
        >
          <FilterIcon width={16} height={16} />
        </AntDButton>
      </div>
      <div className={styles.dateWrapper}>
        <AppointmentDatePicker
          date={[dayjs(start), dayjs(end)]}
          setDate={(date) => {
            setSearchParams(
              (prev) => {
                prev.set("start", date[0].toISOString());
                prev.set("end", date[1].toISOString());
                return prev;
              },
              {
                replace: true,
              }
            );
          }}
        />
      </div>
      <div className={styles.filterBadges}>
        {selectedCategory?.id ? (
          <AntDButton
            size="small"
            className={styles.badgeButton}
            type="text"
            onClick={() => {
              setSelectedCategory({ id: null, name: null });
              setValue(null);
            }}
          >
            {selectedCategory.name}
            <CancelIcon className={styles.cancelIcon} />
          </AntDButton>
        ) : null}
        {sort ? (
          <AntDButton
            size="small"
            className={styles.badgeButton}
            type="text"
            onClick={() => {
              setSearchParams((prev) => {
                prev.delete("sort");
                return prev;
              });
            }}
          >
            {t(`common.${sort_items[sort]}`)}
            <CancelIcon className={styles.cancelIcon} />
          </AntDButton>
        ) : null}
      </div>
      <div>
        {appointmentList?.length > 0 && !appointmentsLoading ? (
          appointmentList.map((item) => <AppointmentCard {...item} />)
        ) : appointmentList?.length === 0 && !appointmentsLoading ? (
          <span className={styles.CardEmpty}>{t("appointment.empty")}</span>
        ) : (
          <div className={styles.Loading}>
            <Loading />
          </div>
        )}
      </div>
      <FilterDrawer
        open={filtersOpen}
        closeHandler={closeDrawer}
        clearFilter={clearFilter}
        submitFilter={submitFilter}
      >
        {loading ? (
          <div className={styles.Loading}>
            <Loading />
          </div>
        ) : (
          <Collapse
            ghost
            expandIconPosition="end"
            expandIcon={({ isActive }) => (
              <BottomArrowIcon
                transform={`rotate(${isActive ? 180 : 0})`}
                width="10px"
              />
            )}
            items={items}
          />
        )}
      </FilterDrawer>
    </>
  );
}
